import { Field, Form, Formik } from "formik";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import { bool } from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as i18n from "util/i18n";
import MainContainer from "pages/_components/MainContainer";
// eslint-disable-next-line import/no-named-as-default
import ResponsiveHeader, { DesktopButtons, MobileButtons } from "pages/_components/header";
import { actions as comexActions } from "reducers/comex";
import { selectors as sessionSelectors } from "reducers/session";
import { store } from "store";
import CustomText from "pages/_components/CustomText/CustomText";
import Image from "pages/_components/Image";
import I18n from "pages/_components/I18n";
import Action from "pages/_components/Action";
import { push } from "connected-react-router";
import ComexStepper from "../_components/ComexStepper";
import DocumentUploader from "../_components/DocumentUploader";
// eslint-disable-next-line import/no-named-as-default
import useComexAditionalDocs from "./hooks/userComexAditionalDocs";

const FORM_ID = "comex.aditionalDocumentation";
const DOCUMENT_FILE_COUNT = 10;
const StepGoodsDocumentation = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const {
        codeStatute,
        codeCertificate,
        codeAccountingCertificate,
        codeDdjj,
        codeMiPyme,
        codeCTIT,
        codeSDEDI,
        codeImportDispatch,
        codeComercialFact,
        codeDeliveryDoc,
        ...comexDocs
    } = useComexAditionalDocs();

    const { comexGoodsDocumentation = {}, comexId = null, comexTransfer = {} } = useSelector((state) => state.comex);

    const username = sessionSelectors.getUserFullName(store.getState());
    const title = i18n.get("comex.request.title").replace("#{NAME}", username);
    const [isSaved, setIsSaved] = useState(false);
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);

    const DOCUMENTS = [
        "documentImportDispatch",
        "documentComercialFact",
        "documentDeliveryDoc",
        "documentCertificate",
        "documentAccountingCertificate",
        "documentDdjj",
        "documentMiPyme",
        "documentCTIT",
        "documentSDEDI",
        "documentStatute",
    ];

    const initialValues = {
        ...Object.fromEntries([
            ...DOCUMENTS.flatMap((key) => [
                [key, comexGoodsDocumentation?.[key] ?? ""],
                [`${key}Name`, comexGoodsDocumentation?.[`${key}Name`] ?? ""],
            ]),

            ...Array.from({ length: DOCUMENT_FILE_COUNT }, (_, index) => {
                const fileNumber = index + 1;
                return [
                    [`documentFile${fileNumber}`, comexGoodsDocumentation?.[`documentFile${fileNumber}`] ?? ""],
                    [`documentFile${fileNumber}Name`, comexGoodsDocumentation?.[`documentFile${fileNumber}Name`] ?? ""],
                ];
            }).flat(),
        ]),
    };

    const [formValues, setFormValues] = useState(initialValues);

    useEffect(() => {
        const allInitialEmpty = Object.values(initialValues).every((value) => value === "");
        const hasChanged = Object.keys(initialValues).some(
            (key) => JSON.stringify(formValues[key]) !== JSON.stringify(initialValues[key]),
        );
        // Habilitar el botón si todos los initialValues son "" o si ha cambiado algo
        setIsSaved(!(allInitialEmpty || hasChanged));

        // Habilitar continuar solo si los documentos obligatorios fueron completados
        const isFormCompleteMin =
            formValues.documentImportDispatch && formValues.documentComercialFact && formValues.documentDeliveryDoc;

        setIsSaveEnabled(isFormCompleteMin);
    }, [formValues]);

    const saveComexRequest = (isContinue) => {
       

        dispatch(comexActions.setComexGoodsDocument(formValues));
 
        const documents = Object.keys(formValues).reduce((acc, key) => {
            const value = formValues[key];
            if (value !== "" && value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});

        const _comexRequest = {
            ...comexTransfer,
            goodsDocumentation: {
                ...documents,
            },
            step: "stepGOODSDOCUMENT",
            comexId,
            isContinue,
        };
        dispatch(comexActions.setComexTransfer(_comexRequest));
        dispatch(comexActions.createComexTransfer(_comexRequest));
        setIsSaved(true);
    };

    const handleSubmit = (values, formikBag) => {
        formikBag.setSubmitting(false);
        saveComexRequest(true);
        dispatch(push("/comex/ddjj/goods"));
    };

    const requiredDocs = [
        {
            key: "documentImportDispatch",
            labelKey: `${FORM_ID}.importDispatch.label`,
            code: codeImportDispatch,
            documentId: comexGoodsDocumentation?.documentImportDispatch,
            documentName: comexGoodsDocumentation?.documentImportDispatchName,
            newdocname: "DespachoImportacion",
        },
        {
            key: "documentComercialFact",
            labelKey: `${FORM_ID}.comercialFact.label`,
            code: codeComercialFact,
            documentId: comexGoodsDocumentation?.documentComercialFact,
            documentName: comexGoodsDocumentation?.documentComercialFactName,
            newdocname: "FacturaComercial",
        },
        {
            key: "documentDeliveryDoc",
            labelKey: `${FORM_ID}.deliveryDoc.label`,
            code: codeDeliveryDoc,
            documentId: comexGoodsDocumentation?.documentDeliveryDoc,
            documentName: comexGoodsDocumentation?.documentDeliveryDocName,
            newdocname: "DocumentoTransporte",
        },
    ];

    const associatedDocs = [
        {
            key: "documentCertificate",
            labelKey: `${FORM_ID}.validationcertificate.label`,
            code: codeCertificate,
            documentId: comexGoodsDocumentation?.documentCertificate,
            documentName: comexGoodsDocumentation?.documentCertificateName,
            newdocname: "CertificadoValidacion",
        },
        {
            key: "documentAccountingCertificate",
            labelKey: `${FORM_ID}.accountingcertificate.label`,
            code: codeAccountingCertificate,
            documentId: comexGoodsDocumentation?.documentAccountingCertificate,
            documentName: comexGoodsDocumentation?.documentAccountingCertificateName,
            newdocname: "CertificacionContableDeuda",
        },
        {
            key: "documentDdjj",
            labelKey: `${FORM_ID}.ddjjaclaratoria.label`,
            code: codeDdjj,
            documentId: comexGoodsDocumentation?.documentDdjj,
            documentName: comexGoodsDocumentation?.documentDdjjName,
            newdocname: "DDJJAclaratoria",
        },
        {
            key: "documentMiPyme",
            labelKey: `${FORM_ID}.mipymecertificate.label`,
            code: codeMiPyme,
            documentId: comexGoodsDocumentation?.documentMiPyme,
            documentName: comexGoodsDocumentation?.documentMiPymeName,
            newdocname: "CertificadoMIPYME",
        },
        {
            key: "documentCTIT",
            labelKey: `${FORM_ID}.ctitcertificate.label`,
            code: codeCTIT,
            documentId: comexGoodsDocumentation?.documentCTIT,
            documentName: comexGoodsDocumentation?.documentCTITName,
            newdocname: "CertificadoTipificacionImportacion",
        },
        {
            key: "documentSDEDI",
            labelKey: `${FORM_ID}.sdedi.label`,
            code: codeSDEDI,
            documentId: comexGoodsDocumentation?.documentSDEDI,
            documentName: comexGoodsDocumentation?.documentSDEDIName,
            newdocname: "SEDI",
        },
    ];

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={6} type="goods" />}
            <Notification scopeToShow="ComexNotification" />
            <ResponsiveHeader ticket metaTitle="transfers.comex.title" backLinkTo="/comex/retention">
                <DesktopButtons>
                    <Button
                        className="btn btn-outline btn-block small"
                        onClick={() => saveComexRequest()}
                        disabled={isSaved}>
                        <Image
                            src={isSaved ? "images/figma/icon/check.svg" : "images/figma/icon/save.svg"}
                            className="icon-medium svg-icon"
                        />
                        <I18n id={isSaved ? "comex.transfer.save.ok.btn" : "comex.transfer.save.btn"} />
                    </Button>
                </DesktopButtons>
                <MobileButtons>
                    <Action labelKey="comex.transfer.save.btn" handleClick={saveComexRequest} disabled={isSaved} />
                </MobileButtons>
            </ResponsiveHeader>

            <MainContainer showLoader={false}>
                <Notification scopeToShow="UploadNotification" />
                <h2>{title}</h2>
                <I18n component="h3" id="comex.documentation.title" />
                <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                    {({ isSubmitting, values }) => {
                        setFormValues(values);
                        return (
                            <Form>
                                <GridTable className="form">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={4}
                                            rowStart={1}>
                                            <CustomText
                                                text="comex.required.documentation.subtitle"
                                                textType="I18N"
                                                className="retention-subtitle mg-left"
                                            />
                                        </GridTable.Data>

                                        {requiredDocs.map((doc, index) => (
                                            <GridTable.Data
                                                key={doc.key}
                                                columnStart={2}
                                                rowStart={2 + index}
                                                flexDirection="column"
                                                columnWidth={10}>
                                                <CustomText
                                                    className="document-title"
                                                    text={doc.labelKey}
                                                    textType="I18N"
                                                />
                                                <Field
                                                    component={DocumentUploader}
                                                    idForm={FORM_ID}
                                                    name={doc.key}
                                                    label="file.upload.input.label"
                                                    extensionDocument="pdf"
                                                    documentCode={doc.code}
                                                    defaultDocument={{
                                                        documentId: doc.documentId,
                                                        documentName: doc.documentName,
                                                    }}
                                                    withDocumentName
                                                    overrideDocumentName={doc.newdocname}
                                                />
                                            </GridTable.Data>
                                        ))}

                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                text="comex.associated.documentation.subtitle"
                                                textType="I18N"
                                                className="retention-subtitle mg-left"
                                            />
                                        </GridTable.Data>

                                        {associatedDocs.map((doc, index) => (
                                            <GridTable.Data
                                                key={doc.key}
                                                columnStart={2}
                                                rowStart={6 + index}
                                                flexDirection="column"
                                                columnWidth={10}>
                                                <CustomText
                                                    className="document-title"
                                                    text={doc.labelKey}
                                                    textType="I18N"
                                                />
                                                <Field
                                                    component={DocumentUploader}
                                                    idForm={FORM_ID}
                                                    name={doc.key}
                                                    label="file.upload.input.label"
                                                    extensionDocument="pdf"
                                                    documentCode={doc.code}
                                                    defaultDocument={{
                                                        documentId: comexGoodsDocumentation?.[doc.key],
                                                        documentName: comexGoodsDocumentation?.[`${doc.key}Name`],
                                                    }}
                                                    withDocumentName
                                                    overrideDocumentName={doc.newdocname}
                                                />
                                            </GridTable.Data>
                                        ))}

                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={4}
                                            rowStart={12}>
                                            <CustomText
                                                text="comex.documentation.subtitle"
                                                textType="I18N"
                                                className="retention-subtitle mg-left"
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={2} flexDirection="column" columnWidth={10}>
                                            <CustomText
                                                className="document-title"
                                                text={`${FORM_ID}.statute.label`}
                                                textType="I18N"
                                            />
                                            <Field
                                                component={DocumentUploader}
                                                idForm={FORM_ID}
                                                name="documentStatute"
                                                label="file.upload.input.label"
                                                extensionDocument="pdf"
                                                documentCode={codeStatute}
                                                defaultDocument={{
                                                    documentId: comexGoodsDocumentation?.documentStatute,
                                                    documentName: comexGoodsDocumentation?.documentStatuteName,
                                                }}
                                                withDocumentName
                                            />
                                        </GridTable.Data>

                                        {Array.from({ length: DOCUMENT_FILE_COUNT }, (_, index) => {
                                            const fileNumber = index + 1;
                                            const documentKey = `documentFile${fileNumber}`;
                                            return (
                                                <GridTable.Data
                                                    key={fileNumber}
                                                    columnStart={2}
                                                    flexDirection="column"
                                                    columnWidth={10}>
                                                    <CustomText
                                                        className="document-title"
                                                        text={`${FORM_ID}.fileUpload${fileNumber}`}
                                                        textType="I18N"
                                                    />
                                                    <Field
                                                        component={DocumentUploader}
                                                        idForm={FORM_ID}
                                                        name={documentKey}
                                                        label="file.upload.input.label"
                                                        extensionDocument="pdf"
                                                        documentCode={comexDocs[`codeFile${fileNumber}`] + "_" + fileNumber}
                                                        defaultDocument={{
                                                            documentId: comexGoodsDocumentation?.[documentKey],
                                                            documentName:
                                                                comexGoodsDocumentation?.[`${documentKey}Name`],
                                                        }}
                                                        withDocumentName
                                                    />
                                                </GridTable.Data>
                                            );
                                        })}
                                    </GridTable.Container>
                                </GridTable>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            loading={isSubmitting}
                                            disabled={!isSaveEnabled}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

StepGoodsDocumentation.propTypes = {
    isDesktop: bool.isRequired,
};

export default StepGoodsDocumentation;
